/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import { useLayout } from '../../../../layout/core'
import { KTIcon } from '../../../../helpers'
import { axiosClientAuth } from '../../../../../app/utils'
import Swal from 'sweetalert2'
import { getClassNameByStatus } from '../../../../../app/utils/helper'
import RejectionReasonModal from '../modal/RejectionReasonModal'
import { PE_TM_QUERY_KEY } from '../../../../../app/pages/IPList/ipQueries'
import { useAuth } from '../../../../../app/modules/auth'
import {REGULATORY_OPERATOR_KEY} from '../../../../../config/countryConfig'

const DrawerIPList: FC = () => {
  const intl = useIntl()
  const [data, setData] = useState([])
  const [flag, setFlag] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionUuid, setRejectionUuid] = useState<string | null>(null)
  const [actionType, setActionType] = useState<'whitelist' | 'blacklist' | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const { uuidOfVendorForIpList, showLoader, hideLoader } = useLayout()
  const { checkPermission } = useAuth()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!uuidOfVendorForIpList) return
    setSearchTerm('')

    axiosClientAuth
      .get('/ip/', {
        params: { owner_id: uuidOfVendorForIpList, page_size: 100 },
        transformResponse: [
          (data: string) => {
            const safe = data.replace(/"id"\s*:\s*(\d{15,})/g, '"id":"$1"')
            return JSON.parse(safe)
          },
        ],
      })
      .then((res) => {
        const mapped = res.data.data.results.map((item: any) => ({
          uuid: String(item.id),
          name: item.title,
          address: item.address,
          is_blacklisted_by_tcil:
            item.status?.[REGULATORY_OPERATOR_KEY]?.active == null
              ? null
              : !item.status?.[REGULATORY_OPERATOR_KEY]?.active,
          created_at: item.created_at,
        }))
        setData(mapped)
      })
      .catch((error) => console.log(error))
  }, [uuidOfVendorForIpList, flag])

  const handleRejectionSubmit = async (reason: string) => {
    if (!rejectionUuid) return
    showLoader()
    try {
      await axiosClientAuth.patch(`/ip/${rejectionUuid}/status/`, {
        active: actionType === 'whitelist',
        reason,
      })
      setFlag((f) => !f)
      queryClient.invalidateQueries(PE_TM_QUERY_KEY)
      Swal.fire({
        titleText: intl.formatMessage({ id: 'SWAL.SUCCESS_TITLE', defaultMessage: 'Success!' }),
        text: actionType === 'blacklist'
          ? intl.formatMessage({ id: 'DRAWER.IP_BLACKLISTED_SUCCESS', defaultMessage: 'IP Blacklisted Successfully' })
          : intl.formatMessage({ id: 'DRAWER.IP_WHITELISTED_SUCCESS', defaultMessage: 'IP Whitelisted Successfully' }),
        icon: 'success',
        confirmButtonText: intl.formatMessage({ id: 'SWAL.OK_BUTTON', defaultMessage: 'Ok' }),
      })
    } catch (err) {
      console.log(err)
      Swal.fire({
        titleText: intl.formatMessage({ id: 'SWAL.ERROR_TITLE', defaultMessage: 'Error!' }),
        text: intl.formatMessage({ id: 'DRAWER.INTERNAL_SERVER_ERROR', defaultMessage: 'Internal Server Error!!!' }),
        icon: 'error',
        confirmButtonText: intl.formatMessage({ id: 'SWAL.OK_GOT_IT', defaultMessage: 'Ok! Got It' }),
      })
    } finally {
      setRejectionUuid(null)
      setActionType(null)
      setShowRejectModal(false)
      hideLoader()
    }
  }

  const onStatusChange = (item: any, type: boolean) => {
    setRejectionUuid(item.uuid)
    setActionType(type ? 'blacklist' : 'whitelist')
    setShowRejectModal(true)
  }

  return (
    <div
      id='kt_drawer_ip_list'
      className='bg-body'
      data-kt-drawer='true'
      data-kt-drawer-name='chat'
      data-kt-drawer-activate='true'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width="{default:'300px', 'md': '500px'}"
      data-kt-drawer-direction='end'
      data-kt-drawer-toggle='#kt_drawer_ip_list_toggle'
      data-kt-drawer-close='#kt_drawer_ip_list_close'
    >
      <div className='card w-100 rounded-0 shadow-none border-0' id='kt_drawer_ip_list_messenger'>
        <div className='card-header pe-5' id='kt_drawer_ip_list_messenger_header'>
          <div className='card-title'>
            <div className='d-flex justify-content-between flex-row align-items-center gap-4 me-3'>
              <h1>{intl.formatMessage({ id: 'DRAWER.IP_LIST', defaultMessage: 'IP List' })}</h1>
              <div className='d-flex align-items-center position-relative my-1 ms-2'>
                <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
                <input
                  type='text'
                  className='form-control form-control-solid form-select-sm w-150px ps-9'
                  placeholder={intl.formatMessage({ id: 'DRAWER.SEARCH_IP', defaultMessage: 'Search IP' })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className='card-toolbar'>
            <div
              className='btn btn-sm btn-icon btn-active-light-primary'
              id='kt_drawer_ip_list_close'
            >
              <KTIcon iconName='cross' className='fs-2' />
            </div>
          </div>
        </div>

        <div className='card-body'>
          <div className='timeline'>
            <div className='timeline'>
              {data
                ?.filter(
                  (ip: any) =>
                    ip?.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    ip?.name?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((ip: any) => (
                  <div key={ip.uuid} className='row'>
                    <div className='col-8'>
                      <div className='timeline-item'>
                        <div className='timeline-line w-40px'></div>
                        <div className='timeline-icon symbol symbol-circle symbol-40px me-4'>
                          <div
                            className={`symbol-label bg-light-${getClassNameByStatus(
                              ip?.is_blacklisted_by_tcil
                            )}`}
                          >
                            <KTIcon
                              iconName='fasten'
                              className={`fs-2 text-${getClassNameByStatus(
                                ip?.is_blacklisted_by_tcil
                              )}`}
                            />
                          </div>
                        </div>
                        <div className='timeline-content mb-10 mt-2'>
                          <div className='pe-3 mb-5'>
                            <div className='fw-bold text-gray-600 fs-6'>{ip?.name}</div>
                            <div className='fs-5 fw-bold'>{ip?.address}</div>
                            <div className='text-muted fw-semibold'>{ip?.created_at}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {checkPermission('Vendor List', 'can_edit') && (
                      <div className='col-4'>
                        <button
                          className='btn h-35px btn-icon btn btn-light-primary me-3'
                          onClick={() => onStatusChange(ip, false)}
                          disabled={!Boolean(ip?.is_blacklisted_by_tcil)}
                        >
                          <KTIcon iconName='check' className='text-primary text-hover-white fs-1' />
                        </button>
                        <button
                          className='btn h-35px btn-icon btn btn-light-danger me-3'
                          onClick={() => onStatusChange(ip, true)}
                          disabled={Boolean(ip?.is_blacklisted_by_tcil)}
                        >
                          <KTIcon iconName='cross' className='text-danger text-hover-white fs-1' />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {showRejectModal && (
        <RejectionReasonModal
          show={showRejectModal}
          onHide={() => setShowRejectModal(false)}
          onSubmit={handleRejectionSubmit}
          title={actionType === 'whitelist'
            ? intl.formatMessage({ id: 'DRAWER.REASON_FOR_APPROVAL', defaultMessage: 'Enter Reason for Approval' })
            : intl.formatMessage({ id: 'DRAWER.REASON_FOR_REJECTION', defaultMessage: 'Enter Reason for Rejection' })}
          placeholder={actionType === 'whitelist'
            ? intl.formatMessage({ id: 'DRAWER.APPROVAL_REASON_PLACEHOLDER', defaultMessage: 'Enter approval reason' })
            : intl.formatMessage({ id: 'DRAWER.REJECTION_REASON_PLACEHOLDER', defaultMessage: 'Enter rejection reason' })}
        />
      )}
    </div>
  )
}

export { DrawerIPList }
